import { TableDG } from '@alison/material';
import Box from '@mui/material/Box';
import type { Theme } from '@mui/material/styles';
import type { GridColDef, GridRowHeightParams, GridSortModel } from '@mui/x-data-grid';
import { useGridApiRef } from '@mui/x-data-grid';
import { type ReactNode, useMemo } from 'react';
import {
  ActiveDaysCell,
  CreativeCell,
  type CreativeGridRow,
  creativeScoreCellRenderer,
  metricCellRenderer,
  SpendRatioCell,
  TOTALS_ROW_ID,
  toGridRow,
  totalsGridRow,
} from './CreativesTableCells';
import type { CreativesListData, MetricCatalog, MetricTrendScope } from './data/api';
import {
  ACTIVE_DAYS_SORT_FIELD,
  CREATIVE_ID_SORT_FIELD,
  CREATIVE_SCORE_METRIC_NAME,
  type DisplayedMetrics,
  SPEND_RATIO_SORT_FIELD,
} from './data/displayedMetrics';
import { TableEmptyState } from './TableEmptyState';
import {
  GRID_CONTAINER_SX,
  GRID_SX_BASE,
  NO_CREATIVES_MESSAGE,
  pinnedTotalsRowSx,
  stickyColumnSx,
} from './utils';

const DATA_ROW_HEIGHT = 76;

const TOTALS_ROW_HEIGHT = 48;

const METRIC_COLUMN_WIDTH = 120;

const CREATIVE_COLUMN_MAX_WIDTH = 400;

const CREATIVE_COLUMN_MIN_WIDTH = 300;

const CREATIVE_FIELD = 'creative';

const CREATIVE_SCORE_FIELD = 'creativeScore';

const CREATIVE_SCORE_COLUMN_MAX_WIDTH = 200;
const CREATIVE_SCORE_COLUMN_MIN_WIDTH = 150;

function getRowHeight(params: GridRowHeightParams): number {
  return params.id === TOTALS_ROW_ID ? TOTALS_ROW_HEIGHT : DATA_ROW_HEIGHT;
}

function toGridField(sort: string): string {
  if (sort === CREATIVE_ID_SORT_FIELD) return CREATIVE_FIELD;
  if (sort === CREATIVE_SCORE_METRIC_NAME) return CREATIVE_SCORE_FIELD;
  if (sort === ACTIVE_DAYS_SORT_FIELD || sort === SPEND_RATIO_SORT_FIELD) return sort;
  return `metric:${sort}`;
}

function fromGridField(field: string): string {
  if (field === CREATIVE_FIELD) return CREATIVE_ID_SORT_FIELD;
  if (field === CREATIVE_SCORE_FIELD) return CREATIVE_SCORE_METRIC_NAME;
  if (field === ACTIVE_DAYS_SORT_FIELD || field === SPEND_RATIO_SORT_FIELD) return field;
  return field.slice('metric:'.length);
}

const COLUMN_BUFFER_PX = 2000;

const GRID_SX = (theme: Theme) => ({
  ...GRID_SX_BASE(theme),
  ...stickyColumnSx(theme, CREATIVE_FIELD),
  ...pinnedTotalsRowSx(theme, 'top', TOTALS_ROW_HEIGHT),
  '& .MuiDataGrid-row': { cursor: 'pointer' },
});

interface CreativesTableProps {
  data: CreativesListData | undefined;
  catalog: MetricCatalog | undefined;
  applied: DisplayedMetrics;
  pending: boolean;
  trendScope: Omit<MetricTrendScope, 'metric' | 'creativeId'>;
  onSortChange: (metric: string) => void;
  onOpen: (creativeId: string) => void;
  apiRef?: ReturnType<typeof useGridApiRef>;
  leadingKpiLabel: string;
}

export function CreativesTable({
  data,
  catalog,
  applied,
  pending,
  trendScope,
  onSortChange,
  onOpen,
  apiRef: externalApiRef,
  leadingKpiLabel,
}: CreativesTableProps): ReactNode {
  const ownApiRef = useGridApiRef();
  const apiRef = externalApiRef ?? ownApiRef;

  const showCreativeScore = applied.metrics.includes(CREATIVE_SCORE_METRIC_NAME);
  const columns = (
    data?.columns ??
    applied.metrics.map((name) => ({
      name,
      label: catalog?.[name]?.label ?? name,
      format: 'number' as const,
    }))
  ).filter((column) => column.name !== CREATIVE_SCORE_METRIC_NAME);
  const creatives = useMemo(() => data?.creatives ?? [], [data]);
  const currencySymbol = data?.currencySymbol ?? '';

  const gridColumns = useMemo<GridColDef<CreativeGridRow>[]>(() => {
    const creativeColumn: GridColDef<CreativeGridRow> = {
      field: CREATIVE_FIELD,
      headerName: 'CREATIVE',
      minWidth: CREATIVE_COLUMN_MIN_WIDTH,
      maxWidth: CREATIVE_COLUMN_MAX_WIDTH,
      renderCell: CreativeCell,
    };
    const creativeScoreColumn: GridColDef<CreativeGridRow> = {
      field: CREATIVE_SCORE_FIELD,
      headerName: 'CREATIVE SCORE',
      minWidth: CREATIVE_SCORE_COLUMN_MIN_WIDTH,
      maxWidth: CREATIVE_SCORE_COLUMN_MAX_WIDTH,
      align: 'right',
      headerAlign: 'right',
      renderCell: creativeScoreCellRenderer(leadingKpiLabel),
    };
    const activeDaysColumn: GridColDef<CreativeGridRow> = {
      field: ACTIVE_DAYS_SORT_FIELD,
      headerName: 'ACTIVE DAYS',
      minWidth: METRIC_COLUMN_WIDTH,
      align: 'center',
      headerAlign: 'center',
      renderCell: ActiveDaysCell,
    };
    const spendRatioColumn: GridColDef<CreativeGridRow> = {
      field: SPEND_RATIO_SORT_FIELD,
      headerName: 'SPEND RATIO',
      minWidth: METRIC_COLUMN_WIDTH,
      align: 'center',
      headerAlign: 'center',
      renderCell: SpendRatioCell,
    };
    const metricColumns: GridColDef<CreativeGridRow>[] = columns.map((column) => ({
      field: `metric:${column.name}`,
      headerName: column.label,
      minWidth: METRIC_COLUMN_WIDTH,
      align: 'right',
      headerAlign: 'right',
      renderCell: metricCellRenderer(column, catalog, currencySymbol, trendScope),
    }));
    return [
      creativeColumn,
      activeDaysColumn,
      spendRatioColumn,
      ...(showCreativeScore ? [creativeScoreColumn] : []),
      ...metricColumns,
    ];
  }, [columns, catalog, currencySymbol, trendScope, showCreativeScore, leadingKpiLabel]);

  const gridRows = useMemo<CreativeGridRow[]>(() => creatives.map(toGridRow), [creatives]);

  const pinnedTopRow = useMemo(
    () =>
      data === undefined || creatives.length === 0
        ? undefined
        : totalsGridRow(data.totals, creatives.length),
    [data, creatives.length],
  );

  const sortModel: GridSortModel =
    applied.sort === '' ? [] : [{ field: toGridField(applied.sort), sort: applied.direction }];

  function handleSortModelChange(model: GridSortModel): void {
    const next = model[0];
    if (next === undefined) return;
    onSortChange(fromGridField(next.field));
  }

  return (
    <Box>
      <Box sx={GRID_CONTAINER_SX}>
        <TableDG
          apiRef={apiRef}
          columns={gridColumns}
          rows={gridRows}
          pinnedTopRow={pinnedTopRow}
          columnBufferPx={COLUMN_BUFFER_PX}
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          sortingOrder={['asc', 'desc']}
          sortingMode="server"
          loading={pending}
          disableColumnMenu
          disableRowSelectionOnClick
          getRowHeight={getRowHeight}
          onRowClick={(params) => {
            const row = params.row as CreativeGridRow;
            if (row.creative !== null) onOpen(row.creative.creativeId);
          }}
          hideFooter
          sx={GRID_SX}
          slots={{ noRowsOverlay: TableEmptyState }}
          slotProps={{ noRowsOverlay: { children: NO_CREATIVES_MESSAGE } }}
        />
      </Box>
    </Box>
  );
}
